import React, { useEffect, useMemo, useRef, useState } from "react";
import { createAppKit } from "@reown/appkit/react";
import { WagmiProvider, useAccount, useDisconnect } from "wagmi";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { bsc, polygon, polygonAmoy, bscTestnet } from "@reown/appkit/networks";
import { WagmiAdapter } from "@reown/appkit-adapter-wagmi";
import { useDispatch, useSelector } from "react-redux";
import {
  setAccountRequest,
  setAccountSuccess,
  setAccountFailure,
} from "../Actions/accountActions";
import { http, fallback } from "viem";

// ----- CONFIG -----
const PROJECT_ID = "13db330e5ce1624533558566b8ba1de9";

const ACTIVE_NETWORKS = [bsc];
// const ACTIVE_NETWORKS = [bscTestnet];
// const ACTIVE_NETWORKS = [polygonAmoy];

// Multiple reliable RPCs → fewer “stuck” connects across wallets
const TRANSPORTS = {
  [bsc.id]: fallback([
    http("https://bsc-dataseed.binance.org"),
    http("https://bsc-dataseed1.binance.org"),
    http("https://bsc-dataseed2.binance.org"),
    http("https://bsc-dataseed3.binance.org"),
    http("https://rpc.ankr.com/bsc"),
  ]),
  // [bscTestnet.id]: fallback([
  //   // ✅ Testnet RPCs
  //   http("https://bsc-testnet.public.blastapi.io"),
  //   http("https://data-seed-prebsc-1-s1.binance.org:8545"),
  //   http("https://data-seed-prebsc-2-s1.binance.org:8545"),
  //   http("https://endpoints.omniatech.io/v1/bsc/testnet/public"),
  //   http("https://rpc.ankr.com/bsc_testnet_chapel"),
  // ]),
  // [polygonAmoy.id]: fallback([
  //   // ✅ Polygon Amoy Testnet RPCs
  //   http("https://rpc-amoy.polygon.technology"),
  //   http("https://polygon-amoy-bor-rpc.publicnode.com"),
  //   http("https://polygon-amoy.gateway.tenderly.co"),
  //   http("https://rpc.ankr.com/polygon_amoy"),
  // ]),
};

const THEME_VARS = {
  "--w3m-accent": "#1e40af",
  "--w3m-border-radius-master": "8px",
  "--w3m-font-family": "Inter, sans-serif",
};

// Single Query Client instance
const queryClient = new QueryClient();

// Build Wagmi adapter once (memoized)
function useWagmiAdapter() {
  return useMemo(
    () =>
      new WagmiAdapter({
        networks: ACTIVE_NETWORKS,
        projectId: PROJECT_ID,
        transports: TRANSPORTS,
        ssr: true,
      }),
    []
  );
}

// Initialize AppKit exactly once on the client
function useInitAppKit(wagmiAdapter) {
  const initedRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (initedRef.current) return;
    if (!wagmiAdapter?.wagmiConfig) return;

    createAppKit({
      adapters: [wagmiAdapter],
      networks: ACTIVE_NETWORKS,
      projectId: PROJECT_ID,
      defaultChain: bsc,
      // defaultChain: bscTestnet,
      // defaultChain: polygonAmoy,
      features: {
        analytics: true,
        email: false,
        socials: false,
      },
      themeVariables: THEME_VARS,
    });

    initedRef.current = true;
  }, [wagmiAdapter]);
}

// ---------- PROVIDERS ----------
export function AppKitProvider({ children }) {
  const wagmiAdapter = useWagmiAdapter();
  useInitAppKit(wagmiAdapter);

  // Guard while adapter config becomes available
  if (!wagmiAdapter?.wagmiConfig) {
    return <div>Loading wallet configuration...</div>;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <WagmiProvider config={wagmiAdapter.wagmiConfig}>
        {children}
      </WagmiProvider>
    </QueryClientProvider>
  );
}
